import React, { useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row } from "react-bootstrap";

import { getBirthdate, isMinorByBirthdate } from "./document-intake-prefill";
import { getIdentityRequirements, getWizardProfile } from "./document-intake-profiles";
import { collectAnalyses } from "./document-intake-state";
import { DOCUMENT_INTAKE_CSS } from "./document-intake-styles";
import DocumentSlot from "./document-slot";
import IntakeAiBanner from "./intake-ai-banner";
import { useDocumentUploads } from "./use-document-uploads";

const STEP = { DOCUMENT: "document", BIRTHDATE: "birthdate", IDENTITY: "identity" };

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Asistente de carga de documentos del alta de un caso, en pasos:
 *  1. El documento que trae la fecha de nacimiento (carta o aviso de accidente, segun el perfil).
 *  2. Solo si ese documento no la trae: se pregunta si el paciente es menor y se captura la fecha.
 *  3. La identificacion que corresponde a la edad: del padre o tutor si es menor; si no, la del
 *     asegurado (INE o pasaporte), junto con los documentos extra del perfil.
 *
 * Entrega `onFinish(analyses, { birthdate, minor, manualBirthdate })`. Todo es opcional: en cualquier
 * paso se puede continuar sin subir documentos.
 */
const DocumentIntakeWizard = ({ profile, onFinish, onCancel }) => {
  const wizard = getWizardProfile(profile);
  const { entries, typesByKey, upload, isAnyBusy } = useDocumentUploads(true);
  const [step, setStep] = useState(STEP.DOCUMENT);
  const [manualBirthdate, setManualBirthdate] = useState("");

  const firstAnalyses = collectAnalyses([wizard.first], entries);
  const detectedBirthdate = getBirthdate(firstAnalyses);
  const birthdate = detectedBirthdate || manualBirthdate;
  const minor = isMinorByBirthdate(birthdate);
  const identityRequirements = [...getIdentityRequirements(minor), ...wizard.extra];
  const needsBirthdateStep = detectedBirthdate === "";
  const stepsTotal = step === STEP.DOCUMENT || needsBirthdateStep ? 3 : 2;
  const stepNumber = { [STEP.DOCUMENT]: 1, [STEP.BIRTHDATE]: 2, [STEP.IDENTITY]: needsBirthdateStep ? 3 : 2 }[step];

  const stepRequirements = step === STEP.DOCUMENT ? [wizard.first] : step === STEP.IDENTITY ? identityRequirements : [];
  const busy = isAnyBusy(stepRequirements);

  const next = () => {
    if (step === STEP.DOCUMENT) {
      setStep(needsBirthdateStep ? STEP.BIRTHDATE : STEP.IDENTITY);
    } else if (step === STEP.BIRTHDATE) {
      setStep(STEP.IDENTITY);
    }
  };

  const back = () => {
    if (step === STEP.IDENTITY) {
      setStep(needsBirthdateStep ? STEP.BIRTHDATE : STEP.DOCUMENT);
    } else if (step === STEP.BIRTHDATE) {
      setStep(STEP.DOCUMENT);
    }
  };

  const finish = () => {
    const analyses = collectAnalyses([wizard.first, ...identityRequirements], entries);
    onFinish(analyses, {
      birthdate,
      minor,
      manualBirthdate: detectedBirthdate ? "" : manualBirthdate,
    });
  };

  const renderSlots = (requirements) => (
    <Row>
      {requirements.map((requirement) => (
        <Col md={requirements.length === 1 ? 12 : 6} key={requirement.type} className="mb-3">
          <DocumentSlot
            requirement={requirement}
            type={typesByKey[requirement.type]}
            entry={entries[requirement.type]}
            onFile={(file) => upload(requirement.type, file)}
          />
        </Col>
      ))}
    </Row>
  );

  const renderBody = () => {
    if (step === STEP.DOCUMENT) {
      return (
        <>
          <IntakeAiBanner>
            Empecemos con este documento: la IA lo leerá y, con la fecha de nacimiento del
            paciente, sabremos qué identificación pedirte. Puedes continuar sin él.
          </IntakeAiBanner>
          {renderSlots([wizard.first])}
        </>
      );
    }

    if (step === STEP.BIRTHDATE) {
      return (
        <>
          <Alert variant="info" className="py-2">
            No pudimos leer la fecha de nacimiento del paciente. Captúrala para saber si es menor de edad.
          </Alert>
          <Form.Group>
            <Form.Label className="font-weight-bold">¿El paciente es menor de edad? Fecha de nacimiento</Form.Label>
            <Form.Control
              type="date"
              value={manualBirthdate}
              max={todayIso()}
              onChange={(event) => setManualBirthdate(event.target.value)}
            />
          </Form.Group>
          {manualBirthdate && (
            <div aria-live="polite">
              <Badge variant={minor ? "warning" : "success"}>{minor ? "Menor de edad" : "Mayor de edad"}</Badge>
              <span className="small text-muted ml-2">
                {minor
                  ? "Se pedirá la identificación del padre o tutor."
                  : "Se pedirá la identificación del asegurado."}
              </span>
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {minor ? (
          <Alert variant="warning" className="py-2">
            El paciente es menor de edad: sube la identificación del padre o tutor.
          </Alert>
        ) : (
          <IntakeAiBanner>
            Sube la identificación del asegurado. Si es extranjero, sube su pasaporte; lo revisará una persona.
          </IntakeAiBanner>
        )}
        {renderSlots(identityRequirements)}
      </>
    );
  };

  const analysesLoaded = collectAnalyses(stepRequirements, entries).length > 0;
  const nextLabel = step === STEP.BIRTHDATE
    ? (manualBirthdate ? "Continuar" : "Omitir")
    : (analysesLoaded ? "Continuar" : "Continuar sin documento");

  return (
    <Modal show onHide={onCancel} backdrop="static" centered size="lg">
      <style>{DOCUMENT_INTAKE_CSS}</style>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          Documentos del asegurado
          <span className="small text-muted ml-3">
            Paso {stepNumber} de {stepsTotal}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        {step !== STEP.DOCUMENT && (
          <Button variant="outline-secondary" onClick={back} disabled={busy}>
            Atrás
          </Button>
        )}
        {step === STEP.IDENTITY ? (
          <Button variant="primary" onClick={finish} disabled={busy}>
            {collectAnalyses([wizard.first, ...identityRequirements], entries).length > 0
              ? "Finalizar"
              : "Continuar sin documentos"}
          </Button>
        ) : (
          <Button variant="primary" onClick={next} disabled={busy}>
            {nextLabel}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentIntakeWizard;
